import * as React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import { addFileIcon, warningSignIcon } from '@europrocurement/l2d-icons';
import {
    FlexyIconButton,
    useModal,
    ShortenDisplay,
    ItemActions,
} from '@europrocurement/flexy-components';
import { DateTime } from 'luxon';
import { UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import { Acompte, CollectionInvoice, Reglement } from '@europrocurement/l2d-modules/providers';
import useDossierFacturationActions, {
    isAcompte,
} from '../DossierFacturation/hooks/useDossierFacturationActions';
import {
    ActionWidth,
    ActionWidthMobile,
    DateEnregistrementLibelle,
    DateEnregistrementWidth,
    DateEnregistrementWidthMobile,
    DocumentLieWidth,
    FactureLieeLibelle,
    FactureLieeWidth,
    FactureLieeWidthMobile,
    ModeWidth,
    MontantWidth,
    NoteLibelle,
    NoteWidth,
    NoteWidthMobile,
    ReferenceLibelle,
    ReferenceWidth,
    ReferenceWidthMobile,
    TransactionCBLibelle,
    TransactionCBWidth,
    TransactionCBWidthMobile,
} from './functions';
import { formatEuro } from '../Article/functions';
import ProvisionUpdate from '../ProvisionUpdate/ProvisionUpdate';
import { type DocumentGed } from '../../../hooks/useDossierDocumentFilesApi';
import useBillingActions from '../../../hooks/useBillingActions';

export type LigneProvisionReglementProps = {
    reglement: Acompte | Reglement;
    sx?: SxProps;
    handleAddAttachmentFile?: (reglementId?: number) => void;
    reglementsDocs: DocumentGed[];
};

const LigneProvisionReglement: React.FunctionComponent<LigneProvisionReglementProps> = function (
    props,
) {
    const { reglement, sx, handleAddAttachmentFile, reglementsDocs } = props;
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

    const { modalActions } = useModal();
    const { updateAcompteOrReglement, deleteAcompteOrReglement } = useDossierFacturationActions();
    const { handleValidateInvoice } = useBillingActions();

    const handleUpdateAcompteOrReglement = async (item: Acompte | Reglement) => {
        await updateAcompteOrReglement(item);
        modalActions.close();
    };

    const handleDeleteAcompteOrReglement = async () => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        await deleteAcompteOrReglement(reglement);
        setIsSubmitting(false);
    };
    const { palette } = useTheme();

    const roleChecker = UseKeycloakCheckRole();

    const isActionnable = React.useMemo(
        () => !reglement.transactioncb && isAcompte(reglement),
        [reglement],
    );

    const isActionnableWithRole = React.useMemo(
        () => isActionnable && roleChecker('api-formalite:acompte-update'),
        [isActionnable, roleChecker],
    );

    return (
        <>
            <Box
                key={`reglements_${reglement.id}`}
                sx={{
                    display: {
                        xs: 'none',
                        sm: 'none',
                        md: 'none',
                        lg: 'flex',
                        xl: 'flex',
                    },

                    flexDirection: 'row',
                    width: '100%',
                    padding: '4px 8px 4px 4px',
                    gap: '0',
                    alignItems: 'center',
                    borderRadius: '16px',
                    color: 'text.gris50',
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        width: ModeWidth,
                    }}
                >
                    {reglement.mode}
                </Box>
                <Box
                    sx={{
                        width: MontantWidth,
                    }}
                >
                    {reglement.montant ? formatEuro(Number(reglement.montant)) : formatEuro(0)}
                </Box>
                <Box
                    sx={{
                        width: NoteWidth,
                    }}
                >
                    {reglement.note || ' - '}
                </Box>
                <Box
                    sx={{
                        width: ReferenceWidth,
                    }}
                >
                    {reglement.reference || ' - '}
                </Box>
                <Box
                    sx={{
                        width: DateEnregistrementWidth,
                    }}
                >
                    {reglement.dreception
                        ? DateTime.fromJSDate(reglement.dreception).toLocaleString(
                              DateTime.DATE_SHORT,
                          )
                        : ' - '}
                </Box>

                <Box
                    sx={{
                        width: TransactionCBWidth,
                    }}
                >
                    {reglement.transactioncb ? (
                        <ShortenDisplay
                            tooltipLabel={reglement.transactioncb}
                            text={reglement.transactioncb}
                            shortenLength={10}
                        />
                    ) : (
                        ' - '
                    )}
                </Box>
                <Box
                    sx={{
                        width: FactureLieeWidth,
                    }}
                >
                    {reglement?.detailsReglements
                        ? reglement.detailsReglements.map((detail) => {
                              const hasFacture =
                                  'facture' in detail &&
                                  detail.facture &&
                                  typeof detail.facture === 'object';
                              if (!hasFacture || !detail.facture || !detail.facture.numero)
                                  return ' - ';

                              const key = detail.facture.id ?? detail.facture.numero ?? undefined;

                              return (
                                  <Box
                                      sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                      }}
                                  >
                                      <Typography key={key}>
                                          {detail.facture.numero || ' - '}
                                      </Typography>
                                      {detail.facture.factureEnPreparation ? (
                                          <FlexyIconButton
                                              icon={warningSignIcon}
                                              iconOverwriteProps={{
                                                  color: palette.text.secondary,
                                                  size: '2xs',
                                                  sx: {
                                                      '&:hover': {
                                                          color: palette.warning.dark,
                                                      },
                                                  },
                                              }}
                                              onClick={() => {
                                                  if (!detail?.facture) return;

                                                  handleValidateInvoice(
                                                      detail.facture as unknown as CollectionInvoice,
                                                  );
                                              }}
                                              confirmation
                                              normalHoverColor={palette.warning.main}
                                              displayTooltip
                                              tooltipOverwriteProps={{
                                                  title: (
                                                      <Box
                                                          sx={{
                                                              display: 'flex',
                                                              flexDirection: 'column',
                                                              alignItems: 'start',
                                                              justifyContent: 'center',
                                                          }}
                                                      >
                                                          <Typography
                                                              variant="l2dcaption"
                                                              fontWeight="700"
                                                          >
                                                              Attention: Cette facture est
                                                              provisoire.
                                                          </Typography>
                                                          <Typography variant="l2dcaption">
                                                              La facture peut-être consolidée en
                                                              cliquant ici.
                                                          </Typography>
                                                      </Box>
                                                  ),
                                              }}
                                          />
                                      ) : null}
                                  </Box>
                              );
                          })
                        : ' - '}
                </Box>
                <Box
                    sx={{
                        width: DocumentLieWidth,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'start',
                        }}
                    >
                        {reglementsDocs.length}
                        {reglementsDocs && handleAddAttachmentFile ? (
                            <FlexyIconButton
                                icon={addFileIcon}
                                iconOverwriteProps={{
                                    color: palette.text.secondary,
                                    size: '2xs',
                                    sx: {
                                        '&:hover': {
                                            color: palette.primary.dark,
                                        },
                                    },
                                }}
                                onClick={() => {
                                    handleAddAttachmentFile(reglement.id);
                                }}
                                normalHoverColor={palette.primary.main}
                            />
                        ) : null}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: ActionWidth,
                    }}
                >
                    <ItemActions
                        onEdit={
                            isActionnableWithRole
                                ? () => {
                                      modalActions.call(
                                          <ProvisionUpdate
                                              onUpdate={handleUpdateAcompteOrReglement}
                                              acompte={reglement as Acompte}
                                          />,
                                      );
                                  }
                                : undefined
                        }
                        onDelete={
                            isActionnableWithRole
                                ? () => {
                                      handleDeleteAcompteOrReglement();
                                  }
                                : undefined
                        }
                        deleteTooltip="Supprimer cet encaissement"
                        confirmation
                    />
                </Box>
            </Box>

            <Box
                key={`reglement_${reglement.id}`}
                sx={{
                    display: {
                        xs: 'flex',
                        sm: 'flex',
                        md: 'flex',
                        lg: 'none',
                        xl: 'none',
                    },

                    flexDirection: 'column',
                    width: '100%',
                    padding: '4px 8px 4px 4px',
                    gap: '10px',
                    borderRadius: '16px',
                    alignItems: 'center',
                    color: 'text.gris50',
                    marginTop: '10px',
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: ModeWidth,
                        }}
                    >
                        {reglement.mode}
                    </Box>
                    <Box
                        sx={{
                            width: MontantWidth,
                        }}
                    >
                        {reglement.montant ? formatEuro(Number(reglement.montant)) : formatEuro(0)}
                    </Box>
                    <Box
                        sx={{
                            width: NoteWidthMobile,
                        }}
                    >
                        {`${NoteLibelle} : ${reglement.note}`}
                    </Box>
                    <Box
                        sx={{
                            width: ReferenceWidthMobile,
                        }}
                    >
                        {`${ReferenceLibelle} : ${reglement.reference}`}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: DateEnregistrementWidthMobile,
                        }}
                    >
                        {`${DateEnregistrementLibelle} : ${
                            reglement.dreception
                                ? DateTime.fromJSDate(reglement.dreception).toLocaleString(
                                      DateTime.DATE_SHORT,
                                  )
                                : "Pas de date d'enregistrement"
                        }`}
                    </Box>

                    <Box
                        sx={{
                            width: TransactionCBWidthMobile,
                        }}
                    >
                        {`${TransactionCBLibelle} : ${reglement.transactioncb}`}
                    </Box>
                    <Box
                        sx={{
                            width: FactureLieeWidthMobile,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        <Typography component="span">{`${FactureLieeLibelle} : `}</Typography>
                        <Typography component="span">
                            {reglement?.detailsReglements
                                ? reglement.detailsReglements.map((detail) => {
                                      const hasFacture =
                                          'facture' in detail &&
                                          detail.facture &&
                                          typeof detail.facture === 'object';
                                      if (!hasFacture || !detail.facture || !detail.facture.numero)
                                          return null;

                                      const key =
                                          detail.facture.id ?? detail.facture.numero ?? undefined;

                                      if (detail.facture.factureEnPreparation) {
                                          return (
                                              <Typography
                                                  key={key}
                                                  variant="l2dh7"
                                                  color={palette.grey[500]}
                                              >
                                                  {detail.facture.numero}
                                              </Typography>
                                          );
                                      }
                                      return (
                                          <Typography key={key}>{detail.facture.numero}</Typography>
                                      );
                                  })
                                : null}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: ActionWidthMobile,
                        }}
                    >
                        <ItemActions
                            onEdit={
                                isActionnable
                                    ? () => {
                                          modalActions.call(
                                              <ProvisionUpdate
                                                  onUpdate={handleUpdateAcompteOrReglement}
                                                  acompte={reglement as Acompte}
                                              />,
                                          );
                                      }
                                    : undefined
                            }
                            onDelete={
                                isActionnable
                                    ? () => {
                                          handleDeleteAcompteOrReglement();
                                      }
                                    : undefined
                            }
                            deleteTooltip="Supprimer cet encaissement"
                            confirmation
                        />
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default LigneProvisionReglement;
